import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import LanguageRoute from './components/LanguageRoute';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Certifications from './pages/Certifications';
import ExamPage from './pages/ExamPage';
import SubscriptionPage from './pages/SubscriptionPage';
import AuthCallback from './pages/AuthCallback';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import BillingHistory from './pages/BillingHistory';
import MySubscriptions from './pages/MySubscriptions';
import Pricing from './pages/Pricing';
import AdminPricing from './pages/AdminPricing';
import AdminQuestions from './pages/AdminQuestions';
import AdminSubscriptions from './pages/AdminSubscriptions';
import AdminCertifications from './pages/AdminCertifications';
import AdminSettings from './pages/AdminSettings';
import AdminCoupons from './pages/AdminCoupons';
import AdminUsers from './pages/AdminUsers';
import ViewQuestion from './pages/ViewQuestion';
import Settings from './pages/Settings';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Contact from './pages/Contact';
import FAQ from './pages/FAQ';
import Learn from './pages/Learn';
import CoursePage from './pages/CoursePage';
import CourseLessonPage from './pages/CourseLessonPage';
import SkipToMain from './components/SkipToMain';
import './App.css';

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <div className="min-h-screen flex flex-col">
            <SkipToMain />
            <Navbar />
            <main id="main-content" className="flex-1" tabIndex={-1}>
              <Routes>
                {/* Routes without language prefix (redirects to /en/ for SEO) */}
                <Route path="/" element={<LanguageRoute><Home /></LanguageRoute>} />
                <Route path="/certifications" element={<LanguageRoute><Certifications /></LanguageRoute>} />
                <Route path="/learn" element={<LanguageRoute><Learn /></LanguageRoute>} />
                <Route path="/learn/:courseSlug" element={<LanguageRoute><CoursePage /></LanguageRoute>} />
                <Route path="/learn/:courseSlug/:lessonSlug" element={<LanguageRoute><CourseLessonPage /></LanguageRoute>} />
                <Route path="/pricing" element={<LanguageRoute><Pricing /></LanguageRoute>} />
                <Route path="/login" element={<LanguageRoute><Login /></LanguageRoute>} />
                <Route path="/register" element={<LanguageRoute><Register /></LanguageRoute>} />
                <Route path="/forgot-password" element={<LanguageRoute><ForgotPassword /></LanguageRoute>} />
                <Route path="/reset-password" element={<LanguageRoute><ResetPassword /></LanguageRoute>} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route
                  path="/dashboard"
                  element={
                    <LanguageRoute>
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    </LanguageRoute>
                  }
                />
                <Route path="/exam/:examId/:examName?" element={<LanguageRoute><ExamPage /></LanguageRoute>} />
                <Route
                  path="/subscription/:examId"
                  element={
                    <LanguageRoute>
                      <ProtectedRoute>
                        <SubscriptionPage />
                      </ProtectedRoute>
                    </LanguageRoute>
                  }
                />
                <Route
                  path="/subscription/success"
                  element={
                    <LanguageRoute>
                      <ProtectedRoute>
                        <SubscriptionSuccess />
                      </ProtectedRoute>
                    </LanguageRoute>
                  }
                />
                <Route
                  path="/billing"
                  element={
                    <LanguageRoute>
                      <ProtectedRoute>
                        <BillingHistory />
                      </ProtectedRoute>
                    </LanguageRoute>
                  }
                />
                <Route
                  path="/subscriptions"
                  element={
                    <LanguageRoute>
                      <ProtectedRoute>
                        <MySubscriptions />
                      </ProtectedRoute>
                    </LanguageRoute>
                  }
                />
                <Route
                  path="/admin/pricing"
                  element={
                    <ProtectedRoute>
                      <AdminPricing />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/questions/:examId"
                  element={
                    <ProtectedRoute>
                      <AdminQuestions />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/question/:questionId?"
                  element={
                    <AdminRoute>
                      <ViewQuestion />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/subscriptions"
                  element={
                    <ProtectedRoute>
                      <AdminSubscriptions />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/certifications"
                  element={
                    <ProtectedRoute>
                      <AdminCertifications />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/settings"
                  element={
                    <ProtectedRoute>
                      <AdminSettings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/users"
                  element={
                    <AdminRoute>
                      <AdminUsers />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/coupons"
                  element={
                    <ProtectedRoute>
                      <AdminCoupons />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <LanguageRoute>
                      <ProtectedRoute>
                        <Settings />
                      </ProtectedRoute>
                    </LanguageRoute>
                  }
                />
                <Route path="/terms" element={<LanguageRoute><TermsOfService /></LanguageRoute>} />
                <Route path="/privacy" element={<LanguageRoute><PrivacyPolicy /></LanguageRoute>} />
                <Route path="/contact" element={<LanguageRoute><Contact /></LanguageRoute>} />
                <Route path="/faq" element={<LanguageRoute><FAQ /></LanguageRoute>} />
                
                {/* Language-prefixed routes for SEO */}
                <Route path="/:lang" element={<LanguageRoute><Home /></LanguageRoute>} />
                <Route path="/:lang/certifications" element={<LanguageRoute><Certifications /></LanguageRoute>} />
                <Route path="/:lang/learn" element={<LanguageRoute><Learn /></LanguageRoute>} />
                <Route path="/:lang/learn/:courseSlug" element={<LanguageRoute><CoursePage /></LanguageRoute>} />
                <Route path="/:lang/learn/:courseSlug/:lessonSlug" element={<LanguageRoute><CourseLessonPage /></LanguageRoute>} />
                <Route path="/:lang/pricing" element={<LanguageRoute><Pricing /></LanguageRoute>} />
                <Route path="/:lang/login" element={<LanguageRoute><Login /></LanguageRoute>} />
                <Route path="/:lang/register" element={<LanguageRoute><Register /></LanguageRoute>} />
                <Route path="/:lang/forgot-password" element={<LanguageRoute><ForgotPassword /></LanguageRoute>} />
                <Route path="/:lang/reset-password" element={<LanguageRoute><ResetPassword /></LanguageRoute>} />
                <Route
                  path="/:lang/dashboard"
                  element={
                    <LanguageRoute>
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    </LanguageRoute>
                  }
                />
                <Route path="/:lang/exam/:examId/:examName?" element={<LanguageRoute><ExamPage /></LanguageRoute>} />
                <Route
                  path="/:lang/subscription/:examId"
                  element={
                    <LanguageRoute>
                      <ProtectedRoute>
                        <SubscriptionPage />
                      </ProtectedRoute>
                    </LanguageRoute>
                  }
                />
                <Route
                  path="/:lang/subscription/success"
                  element={
                    <LanguageRoute>
                      <ProtectedRoute>
                        <SubscriptionSuccess />
                      </ProtectedRoute>
                    </LanguageRoute>
                  }
                />
                <Route
                  path="/:lang/billing"
                  element={
                    <LanguageRoute>
                      <ProtectedRoute>
                        <BillingHistory />
                      </ProtectedRoute>
                    </LanguageRoute>
                  }
                />
                <Route
                  path="/:lang/subscriptions"
                  element={
                    <LanguageRoute>
                      <ProtectedRoute>
                        <MySubscriptions />
                      </ProtectedRoute>
                    </LanguageRoute>
                  }
                />
                <Route
                  path="/:lang/settings"
                  element={
                    <LanguageRoute>
                      <ProtectedRoute>
                        <Settings />
                      </ProtectedRoute>
                    </LanguageRoute>
                  }
                />
                <Route path="/:lang/terms" element={<LanguageRoute><TermsOfService /></LanguageRoute>} />
                <Route path="/:lang/privacy" element={<LanguageRoute><PrivacyPolicy /></LanguageRoute>} />
                <Route path="/:lang/contact" element={<LanguageRoute><Contact /></LanguageRoute>} />
                <Route path="/:lang/faq" element={<LanguageRoute><FAQ /></LanguageRoute>} />
              </Routes>
            </main>
            <Footer />
            <CookieConsent />
          </div>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
